import type {
  EmailDeliverySettingsUpdate,
  NotificationGroupInput,
  NotificationEndpointIdentity,
} from '../../settings/model.js';
import type { SettingsPageModel } from './model.js';

export class SettingsRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SettingsRequestError';
  }
}

export interface EmailDeliveryTestResponse {
  success: boolean;
  message: string;
  settings: SettingsPageModel;
}

export class LocalSettingsDataProvider {
  load(identityToken: string): Promise<SettingsPageModel> {
    return requestSettings('/api/settings', 'GET', identityToken);
  }

  updateEmailDelivery(
    identityToken: string,
    input: EmailDeliverySettingsUpdate,
  ): Promise<SettingsPageModel> {
    return requestSettings('/api/settings/email-delivery', 'PUT', identityToken, input);
  }

  testEmailDelivery(
    identityToken: string,
    recipient: string,
  ): Promise<EmailDeliveryTestResponse> {
    return requestEmailDeliveryTest(identityToken, recipient);
  }

  replacePrimaryRecipients(
    identityToken: string,
    recipients: readonly string[],
  ): Promise<SettingsPageModel> {
    return requestSettings('/api/settings/primary-recipients', 'PUT', identityToken, { recipients });
  }

  createGroup(
    identityToken: string,
    input: NotificationGroupInput,
  ): Promise<SettingsPageModel> {
    return requestSettings('/api/settings/notification-groups', 'POST', identityToken, input);
  }

  updateGroup(
    identityToken: string,
    id: string,
    input: NotificationGroupInput,
  ): Promise<SettingsPageModel> {
    return requestSettings(
      `/api/settings/notification-groups/${encodeURIComponent(id)}`,
      'PUT',
      identityToken,
      input,
    );
  }

  deleteGroup(identityToken: string, id: string): Promise<SettingsPageModel> {
    return requestSettings(
      `/api/settings/notification-groups/${encodeURIComponent(id)}`,
      'DELETE',
      identityToken,
    );
  }

  replaceDeviceAssignments(
    identityToken: string,
    organizationId: number,
    deviceId: number,
    groupIds: readonly string[],
  ): Promise<SettingsPageModel> {
    return requestSettings('/api/settings/device-assignments', 'PUT', identityToken, {
      organizationId,
      deviceId,
      groupIds,
    });
  }

  replaceGroupAssignments(
    identityToken: string,
    groupId: string,
    endpoints: readonly NotificationEndpointIdentity[],
  ): Promise<SettingsPageModel> {
    return requestSettings(
      `/api/settings/notification-groups/${encodeURIComponent(groupId)}/device-assignments`,
      'PUT',
      identityToken,
      { endpoints },
    );
  }
}

async function requestEmailDeliveryTest(
  identityToken: string,
  recipient: string,
): Promise<EmailDeliveryTestResponse> {
  const payload = await requestJson(
    '/api/settings/email-delivery/test',
    'POST',
    identityToken,
    { recipient },
  );
  if (!isRecord(payload)
    || typeof payload.success !== 'boolean'
    || typeof payload.message !== 'string'
    || !isSettingsPageModel(payload.settings)) {
    throw new SettingsRequestError('Settings service returned an invalid response');
  }
  return {
    success: payload.success,
    message: payload.message,
    settings: payload.settings,
  };
}

async function requestSettings(
  path: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  identityToken: string,
  body?: unknown,
): Promise<SettingsPageModel> {
  const payload = await requestJson(path, method, identityToken, body);
  if (!isSettingsPageModel(payload)) {
    throw new SettingsRequestError('Settings service returned an invalid response');
  }
  return payload;
}

async function requestJson(
  path: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  identityToken: string,
  body?: unknown,
): Promise<unknown> {
  if (identityToken.length === 0) throw new SettingsRequestError('Authenticated session is unavailable');
  let response: Response;
  try {
    const request: RequestInit = {
      method,
      headers: {
        Authorization: `Bearer ${identityToken}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      cache: 'no-store',
    };
    if (body !== undefined) request.body = JSON.stringify(body);
    response = await fetch(path, request);
  } catch {
    throw new SettingsRequestError('Settings service is unavailable');
  }
  const payload = await response.json().catch(() => null) as unknown;
  if (!response.ok) {
    const message = isRecord(payload) && typeof payload.error === 'string'
      ? payload.error
      : 'Settings could not be saved';
    throw new SettingsRequestError(message);
  }
  return payload;
}

function isSettingsPageModel(value: unknown): value is SettingsPageModel {
  if (!isRecord(value)) return false;
  return isRecord(value.emailDelivery)
    && Array.isArray(value.inventory)
    && Array.isArray(value.primaryBackupAlertRecipients)
    && Array.isArray(value.additionalNotificationGroups)
    && Array.isArray(value.deviceGroupAssignments);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
